import { dayEndMood, moodLabel, signedFigure, type DayGlance } from '@adhd/shared';
import { bandOf, inkOn } from '../lib/band.js';

/**
 * Ten days, as ten coloured blocks: the stretch of time the condition takes
 * away, at a glance. Release 1 of 2.0 (docs/PRD-2.0.md §2).
 *
 * Oldest on the left, today on the right, like a calendar — the opposite of the
 * week strip it replaces on Today, which scrolled newest-first because its
 * cards were wide. These are not; all ten fit a phone, so the shape can be the
 * one everybody already reads.
 *
 * A DAY WITH NOTHING IN IT IS A DASHED EMPTY BLOCK, not a calm one. The strip
 * it replaces said this in its own comment and the rule carries: a gap is a
 * fact about the record, and drawing it as a quiet day invents a week.
 *
 * Each block shows where the day ENDED — dayEndMood, the last check-in — and
 * never a mean, per release 2 of the September work and docs/PRD-2.0.md §6.
 */
export function TenDays({
  days,
  picked,
  onPick,
  today,
}: {
  /** Oldest first. Whatever /insights/days?days=10 returns, sorted here. */
  days: DayGlance[];
  picked: string;
  onPick: (day: string) => void;
  today: string;
}): JSX.Element | null {
  if (days.length === 0) return null;
  const sorted = [...days].sort((a, b) => a.day.localeCompare(b.day));
  return (
    <section className="stack-sm stack" aria-label="The last ten days">
      <div className="row-between">
        <h2 className="ten-h">Ten days</h2>
      </div>
      <ol className="ten" role="list">
        {sorted.map((d) => {
          const end = dayEndMood(d);
          const band = end === null ? null : bandOf(end);
          const date = new Date(`${d.day}T12:00:00`);
          const label =
            d.day === today
              ? 'Today'
              : date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
          const title = `${label} — ${end === null ? (d.empty ? 'nothing logged' : 'no check-in') : `${moodLabel(end)} ${signedFigure(end)}`}`;
          return (
            <li key={d.day}>
              <button
                type="button"
                className={`ten-d${d.day === today ? ' ten-today' : ''}${picked === d.day ? ' ten-picked' : ''}`}
                aria-pressed={picked === d.day}
                aria-label={title}
                title={title}
                onClick={() => onPick(d.day)}
              >
                <span className="ten-w">{date.toLocaleDateString(undefined, { weekday: 'narrow' })}</span>
                <span
                  className={`ten-b${band ? ` ten-${band} ten-ink-${inkOn(band)}` : d.empty ? ' ten-empty' : ' ten-blank'}`}
                >
                  {end === null ? '·' : signedFigure(end)}
                </span>
                <span className="ten-n">{date.getDate()}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
